// Expenses → Import receipts. Drop an expense import file (the JSON template,
// see utils/expense-import.ts and .claude/skills/receipt-import.md) together
// with the receipt files it names; check each expense on a review card; Apply.
//
// Nothing new on the server: each card is saved exactly the way New Expense
// saves one — the receipt uploads first (converted to PDF and named by
// buildReceiptFilename), then POST /api/expenses carries its vault path. A card
// that saved is skipped on a retry, so a partial failure can simply be fixed
// and applied again.

import { useEffect, useMemo, useState } from 'react';
import type { Asset, Project, Vendor } from '@stfors/shared-types';
import { Modal } from '../Modal';
import { FileDrop } from '../FileDrop';
import { VendorPicker } from '../VendorPicker';
import { ProjectOptions } from '../ProjectOptions';
import { MatterSelect } from '../MatterSelect';
import { MoneyInput } from '../MoneyInput';
import { DateInput } from '../DateInput';
import { Toggle } from '../Toggle';
import { DocumentPreviewModal, type PreviewableFile } from '../DocumentPreviewModal';
import { useProjects } from '../../api/queries/projects';
import { useVendors } from '../../api/queries/vendors';
import { useAssets } from '../../api/queries/assets';
import { useLookups, activeOptionsFor } from '../../api/queries/lookups';
import { useCreateExpense, useExpenses } from '../../api/queries/expenses';
import { useUploadDocument } from '../../api/queries/documents';
import { can } from '../../auth/store';
import { apiErrorText } from '../../utils/apiErrors';
import { formatMoney } from '../../utils/money';
import { assetOptionLabel } from '../../utils/asset';
import { tracksMileage } from '../../utils/expense';
import {
  buildReceiptFilename, extensionOf, normalizeReceiptFile, renameFile,
} from '../../utils/receipt';
import {
  parseExpenseImport,
  importDateToEpoch,
  composeImportNotes,
  matchReceiptFile,
  matchProject,
  matchVendor,
  matchOption,
  matchAsset,
  findLikelyDuplicate,
  EXPENSE_IMPORT_SAMPLE,
  type ExpenseImportItem,
  type ParsedImport,
} from '../../utils/expense-import';
import './invoice-ingest.css';
import './expense-import.css';

const RECEIPT_ACCEPT = '.pdf,image/*';

interface Row {
  key: number;
  include: boolean;
  source: ExpenseImportItem;
  errors: string[];
  dateEpoch: number | null;
  vendorId: number | null;
  vendorName: string;
  vendorText: string;
  amount: string;
  tax: string;
  receiptNumber: string;
  type: string;
  taxCategory: string;
  projectId: number | null;
  matterId: number | null;
  reimbursable: boolean;
  mileage: string;
  notes: string;
  assetId: number | null;
  file: File | null;
  /** What the file asked for that the app didn't recognise. */
  unmatched: string[];
  status: 'ready' | 'saving' | 'saved' | 'error';
  saveError: string | null;
  savedId: number | null;
  receiptPath: string | null;
}

function buildRows(
  parsed: ParsedImport,
  files: File[],
  lists: { projects: Project[]; vendors: Vendor[]; assets: Asset[]; types: { value: string; label?: string | null }[];
    taxCats: { value: string; label?: string | null }[]; generics: { value: string; label?: string | null }[] },
): Row[] {
  return parsed.items.map((item, i) => {
    const unmatched: string[] = [];
    const project = matchProject(lists.projects, item.project_case_number);
    if (item.project_case_number && !project) unmatched.push(`project ${item.project_case_number}`);
    const vendor = matchVendor(lists.vendors, item.vendor);
    // No vendor record: a generic stand-in ("Gas station") or a one-off name.
    const generic = vendor ? null : matchOption(lists.generics, item.vendor);
    const type = matchOption(lists.types, item.type);
    if (item.type && !type) unmatched.push(`type "${item.type}"`);
    const taxCat = matchOption(lists.taxCats, item.tax_category);
    const asset = matchAsset(lists.assets, item.asset);
    if (item.asset && !asset) unmatched.push(`asset ${item.asset.serial_number ?? item.asset.name ?? ''}`.trim());
    const file = matchReceiptFile(files, item.receipt_file);
    if (item.receipt_file && !file) unmatched.push(`receipt file ${item.receipt_file}`);
    return {
      key: i,
      include: !parsed.itemErrors[i],
      source: item,
      errors: parsed.itemErrors[i] ?? [],
      dateEpoch: importDateToEpoch(item.date),
      vendorId: vendor?.id ?? null,
      vendorName: vendor?.name ?? '',
      vendorText: vendor ? '' : (generic?.value ?? item.vendor ?? ''),
      amount: item.amount,
      tax: item.tax_amount ?? '',
      receiptNumber: item.receipt_number ?? '',
      type: type?.value ?? '',
      taxCategory: taxCat?.value ?? vendor?.default_tax_category ?? '',
      projectId: project?.id ?? null,
      matterId: null,
      reimbursable: item.reimbursable === true,
      mileage: item.mileage ?? '',
      notes: composeImportNotes(item),
      assetId: asset?.id ?? null,
      file,
      unmatched,
      status: 'ready',
      saveError: null,
      savedId: null,
      receiptPath: null,
    };
  });
}

export function ExpenseImportModal({ onClose }: { onClose: () => void }): JSX.Element {
  const projectsQ = useProjects();
  const vendorsQ = useVendors();
  const assetsQ = useAssets();
  const lookupsQ = useLookups();
  const expensesQ = useExpenses();
  const uploadDoc = useUploadDocument();
  const createExpense = useCreateExpense();

  const [parsed, setParsed] = useState<ParsedImport | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dropError, setDropError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<PreviewableFile | null>(null);

  const lookups = lookupsQ.data?.lookup_options;
  const types = activeOptionsFor(lookups, 'expense_type');
  const taxCats = activeOptionsFor(lookups, 'tax_category');
  const generics = activeOptionsFor(lookups, 'generic_vendor');
  const projects = projectsQ.data?.projects ?? [];
  const assets = assetsQ.data?.assets ?? [];
  const listsReady = !projectsQ.isPending && !vendorsQ.isPending && !assetsQ.isPending && !lookupsQ.isPending;

  // Build the review cards once the file is read and every list it is matched
  // against has loaded. A later drop of more receipt files only attaches them.
  useEffect(() => {
    if (!parsed || parsed.fileErrors.length > 0 || !listsReady || rows !== null) return;
    setRows(buildRows(parsed, files, {
      projects, vendors: vendorsQ.data?.vendors ?? [], assets, types, taxCats, generics,
    }));
  }, [parsed, listsReady, rows]);

  const onDrop = async (dropped: File[]) => {
    setDropError(null);
    const json = dropped.find((f) => f.name.toLowerCase().endsWith('.json'));
    const receipts = dropped.filter((f) => f !== json);
    const nextFiles = [...files.filter((f) => !receipts.some((r) => r.name === f.name)), ...receipts];
    setFiles(nextFiles);
    if (json) {
      const result = parseExpenseImport(await json.text());
      if (result.fileErrors.length > 0) {
        setDropError(`${json.name}: ${result.fileErrors.join(' ')}`);
        return;
      }
      setParsed(result);
      setRows(null);
    } else if (rows) {
      // Receipts dropped after the file: attach them to cards that lack one.
      setRows(rows.map((r) => (r.file || r.status === 'saved'
        ? r
        : { ...r, file: matchReceiptFile(nextFiles, r.source.receipt_file) })));
    }
  };

  const update = (key: number, patch: Partial<Row>) =>
    setRows((prev) => prev && prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const existing = expensesQ.data?.expenses ?? [];
  const duplicates = useMemo(() => {
    const out: Record<number, string> = {};
    for (const r of rows ?? []) {
      if (r.status === 'saved') continue;
      const d = findLikelyDuplicate(existing, r.dateEpoch, r.amount, r.vendorName || r.vendorText || null);
      if (d) out[r.key] = `An expense for ${formatMoney(d.amount)} on this date is already recorded${d.vendor ? ` (${d.vendor})` : ''}.`;
    }
    return out;
  }, [rows, existing]);

  const problemsOf = (r: Row): string[] => {
    const p = [...r.errors];
    if (r.dateEpoch === null) p.push('Pick the date.');
    if (!/^\d+(\.\d{1,2})?$/.test(r.amount) || Number(r.amount) <= 0) p.push('Enter the total paid.');
    if (r.tax !== '' && (!/^\d+(\.\d{1,2})?$/.test(r.tax) || Number(r.tax) > Number(r.amount))) {
      p.push('Sales tax must be a number no larger than the total.');
    }
    if (r.type === '') p.push('Pick a type.');
    return p;
  };

  const toApply = (rows ?? []).filter((r) => r.include && r.status !== 'saved');
  const blocked = toApply.filter((r) => problemsOf(r).length > 0);

  const apply = async () => {
    if (!rows) return;
    setApplying(true);
    for (const r of toApply) {
      if (problemsOf(r).length > 0) continue;
      update(r.key, { status: 'saving', saveError: null });
      try {
        let receiptPath = r.receiptPath;
        if (r.file && !receiptPath) {
          const normalized = await normalizeReceiptFile(r.file);
          const named = renameFile(normalized, buildReceiptFilename({
            caseNumber: projects.find((p) => p.id === r.projectId)?.case_number ?? null,
            dateEpoch: r.dateEpoch!,
            receiptNumber: r.receiptNumber.trim() || null,
            vendor: r.vendorText.trim() || r.vendorName,
            type: r.type,
            ext: extensionOf(normalized),
          }));
          const resp = await uploadDoc.mutateAsync({
            file: named,
            document_type: 'other',
            convert_to_pdf: true,
            ...(r.projectId !== null ? { project_id: r.projectId } : {}),
          });
          receiptPath = resp.document.file_path;
          // Keep it, so a retry after a failed save doesn't upload it twice.
          update(r.key, { receiptPath });
        }
        const { expense } = await createExpense.mutateAsync({
          project_id: r.projectId,
          matter_id: r.projectId === null ? null : r.matterId,
          type: r.type,
          amount: Number(r.amount).toFixed(2),
          tax_amount: r.tax === '' ? '0.00' : Number(r.tax).toFixed(2),
          date: r.dateEpoch!,
          vendor_id: r.vendorId,
          vendor: r.vendorId === null ? (r.vendorText.trim() || undefined) : undefined,
          receipt_number: r.receiptNumber.trim() || undefined,
          tax_category: r.taxCategory.trim() || undefined,
          reimbursable: r.reimbursable || undefined,
          mileage: r.mileage.trim() ? Number(r.mileage).toFixed(2) : undefined,
          notes: r.notes.trim() || undefined,
          receipt_path: receiptPath ?? undefined,
          asset_id: r.assetId,
        });
        update(r.key, { status: 'saved', savedId: expense.id });
      } catch (err) {
        update(r.key, { status: 'error', saveError: apiErrorText(err, 'Could not save this expense.') });
      }
    }
    setApplying(false);
  };

  const saved = (rows ?? []).filter((r) => r.status === 'saved').length;
  const allDone = rows !== null && rows.every((r) => !r.include || r.status === 'saved');

  const footer = rows ? (
    <>
      <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={applying}>
        {allDone ? 'Close' : 'Cancel'}
      </button>
      {!allDone && (
        <button
          type="button"
          className="btn-cyan-primary"
          onClick={() => void apply()}
          disabled={applying || toApply.length === 0 || blocked.length === toApply.length}
        >
          {applying ? 'Saving…' : `Save ${toApply.length - blocked.length} expense${toApply.length - blocked.length === 1 ? '' : 's'}`}
        </button>
      )}
    </>
  ) : (
    <button type="button" className="btn-charcoal-secondary" onClick={onClose}>Cancel</button>
  );

  return (
    <Modal title="Import Receipts" open onClose={onClose} busy={applying} size="lg" footer={footer}>
      <div className="exi-body">
        <FileDrop
          onFiles={(f) => void onDrop(f)}
          multiple
          accept={`.json,${RECEIPT_ACCEPT}`}
          disabled={applying}
          label={parsed ? 'Drop more receipt files here' : 'Drop the import file (.json) and its receipts here'}
          hint="The .json lists the expenses; each names its receipt file (PDF or image)."
        />
        {!parsed && (
          <p className="exi-status">
            Not sure what the file looks like?{' '}
            <button
              type="button"
              className="btn-text"
              onClick={() => {
                const blob = new Blob([JSON.stringify(EXPENSE_IMPORT_SAMPLE, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'expense-import-sample.json';
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              Download a sample
            </button>
          </p>
        )}
        {dropError && <p className="inv-ingest-error" role="alert">{dropError}</p>}
        {parsed && !listsReady && <p className="exi-status">Loading projects, vendors and types…</p>}
        {saved > 0 && <p className="inv-ingest-ok" role="status">{saved} saved.</p>}

        {rows && (
          <ul className="inv-review-list">
            {rows.map((r) => {
              const problems = problemsOf(r);
              const locked = r.status === 'saved' || r.status === 'saving' || applying;
              return (
                <li
                  key={r.key}
                  className={[
                    'inv-review-card',
                    !r.include ? 'is-skipped' : '',
                    r.status === 'error' ? 'is-error' : '',
                    r.status === 'saved' ? 'exi-saved' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="inv-review-card-head">
                    <Toggle
                      checked={r.include}
                      onChange={(v) => update(r.key, { include: v })}
                      label={`${r.source.vendor ?? 'Receipt'} — ${r.amount ? formatMoney(r.amount) : '?'}`}
                      disabled={locked}
                    />
                    <span className="exi-file">
                      {r.file ? (
                        <button
                          type="button"
                          className="btn-text"
                          onClick={() => setPreview({
                            downloadUrl: '', filename: r.file!.name,
                            mimeType: r.file!.type || 'application/pdf', blob: r.file!,
                          })}
                        >
                          {r.file.name}
                        </button>
                      ) : r.source.receipt_file ? (
                        <span className="exi-warn">Receipt {r.source.receipt_file} not dropped yet</span>
                      ) : (
                        <span className="exi-muted">No receipt file</span>
                      )}
                    </span>
                    {r.status === 'saved' && <span className="exi-pill exi-pill-ok">Saved</span>}
                    {r.status === 'saving' && <span className="exi-pill">Saving…</span>}
                  </div>

                  {r.include && r.status !== 'saved' && (
                    <>
                      <div className="exi-grid">
                        <label className="exi-field">
                          <span>Date *</span>
                          <DateInput value={r.dateEpoch} onChange={(v) => update(r.key, { dateEpoch: v })} disabled={locked} />
                        </label>
                        <label className="exi-field">
                          <span>Total paid *</span>
                          <MoneyInput value={r.amount} onChange={(v) => update(r.key, { amount: v })} disabled={locked} aria-label="Total paid" />
                        </label>
                        <label className="exi-field">
                          <span>Sales tax</span>
                          <MoneyInput value={r.tax} onChange={(v) => update(r.key, { tax: v })} disabled={locked} aria-label="Sales tax" />
                        </label>
                        <label className="exi-field">
                          <span>Receipt #</span>
                          <input className="form-input" value={r.receiptNumber} maxLength={120}
                            onChange={(e) => update(r.key, { receiptNumber: e.target.value })} disabled={locked} />
                        </label>
                        <div className="exi-field exi-span2">
                          <span>Vendor</span>
                          <VendorPicker
                            value={r.vendorId}
                            onChange={(id, v) => update(r.key, {
                              vendorId: id,
                              vendorName: v?.name ?? '',
                              ...(v?.default_tax_category && r.taxCategory === '' ? { taxCategory: v.default_tax_category } : {}),
                            })}
                            allowGeneric
                            textValue={r.vendorText}
                            onTextChange={(t) => update(r.key, { vendorText: t })}
                            canAdd={can('vendor.write')}
                            disabled={locked}
                          />
                        </div>
                        <label className="exi-field">
                          <span>Type *</span>
                          <select className="form-input" value={r.type} disabled={locked}
                            onChange={(e) => update(r.key, { type: e.target.value })}>
                            <option value="">Pick a type…</option>
                            {types.map((o) => <option key={o.value} value={o.value}>{o.label ?? o.value}</option>)}
                          </select>
                        </label>
                        <label className="exi-field">
                          <span>Tax category</span>
                          <select className="form-input" value={r.taxCategory} disabled={locked}
                            onChange={(e) => update(r.key, { taxCategory: e.target.value })}>
                            <option value="">None</option>
                            {taxCats.map((o) => <option key={o.value} value={o.value}>{o.label ?? o.value}</option>)}
                          </select>
                        </label>
                        <label className="exi-field exi-span2">
                          <span>Project</span>
                          <select className="form-input" value={r.projectId ?? 0} disabled={locked}
                            onChange={(e) => {
                              const n = Number(e.target.value);
                              update(r.key, { projectId: n > 0 ? n : null, matterId: null });
                            }}>
                            <option value={0}>General business (no project)</option>
                            <ProjectOptions projects={projects} />
                          </select>
                        </label>
                        <MatterSelect
                          className="exi-field"
                          projectId={r.projectId}
                          value={r.matterId}
                          onChange={(m) => update(r.key, { matterId: m })}
                          disabled={locked}
                        />
                        {(tracksMileage(r.type) || r.mileage !== '') && (
                          <label className="exi-field">
                            <span>Miles</span>
                            <input className="form-input" inputMode="decimal" value={r.mileage} disabled={locked}
                              onChange={(e) => update(r.key, { mileage: e.target.value })} />
                          </label>
                        )}
                        {(r.source.asset || r.assetId !== null) && (
                          <label className="exi-field exi-span2">
                            <span>Linked asset</span>
                            <select className="form-input" value={r.assetId ?? 0} disabled={locked}
                              onChange={(e) => {
                                const n = Number(e.target.value);
                                update(r.key, { assetId: n > 0 ? n : null });
                              }}>
                              <option value={0}>None</option>
                              {assets.map((a) => <option key={a.id} value={a.id}>{assetOptionLabel(a)}</option>)}
                            </select>
                          </label>
                        )}
                        <div className="exi-field exi-toggle">
                          <Toggle
                            checked={r.reimbursable}
                            onChange={(v) => update(r.key, { reimbursable: v })}
                            label="Reimbursable (bill to the client)"
                            disabled={locked}
                          />
                        </div>
                        <label className="exi-field exi-span-all">
                          <span>Notes</span>
                          <textarea className="form-input" rows={3} value={r.notes} disabled={locked}
                            onChange={(e) => update(r.key, { notes: e.target.value })} />
                        </label>
                      </div>
                      {r.unmatched.length > 0 && (
                        <p className="exi-warn">Not found in the app: {r.unmatched.join(', ')} — pick it above.</p>
                      )}
                      {duplicates[r.key] && <p className="exi-warn">{duplicates[r.key]} Untick it if this is the same receipt.</p>}
                      {problems.length > 0 && <p className="inv-ingest-error">{problems.join(' ')}</p>}
                      {r.saveError && <p className="inv-ingest-error" role="alert">{r.saveError}</p>}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {preview && <DocumentPreviewModal file={preview} onClose={() => setPreview(null)} />}
    </Modal>
  );
}
